<template>
  <div>
    <div class="w-full">
      <Loading v-if="loading" class="mt-10" />
      <div class="z-20 w-full fixed top-0" :inert="questionModalOpen || upsellModalOpen">
        <MerchantNav
          v-fade-in
          :disable-hide-on-scroll="showCartItemsOnly || shouldDisplayQuestions"
          :empty-middle="true"
          :show-cart-pill="!showCartItemsOnly && !shouldDisplayQuestions"
          @show-cart="handleShowCart"
        >
          <ProgressPills :pills="progressPills" />
        </MerchantNav>
        <MerchantCategoriesBar v-if="!showCartItemsOnly" v-fade-in @show-cart="handleShowCart" />
        <TheBanner
          v-for="(message, i) in messages"
          :key="message.uid"
          :position="message?.position"
          :status="message?.status"
          :use-emoji="message?.useEmoji"
          :title="message?.title"
          :actions="message?.actions"
          :stack-index="i"
          :unique-id="message.uid"
          :dismiss-timeout="message?.dismissTimeout"
          @on-dismiss="handleDismissedBanner(bannerIndex)"
        >
          <template #message>{{ message.message }}</template>
        </TheBanner>
      </div>
      <div v-show="!loading" :inert="questionModalOpen || upsellModalOpen">
        <!-- <MerchantFiltersBar /> -->
        <MerchantMain :in-cart="showCartItemsOnly" @show-full-view="showFullView" />

        <div v-if="openForOrders" class="pb-8">
          <StoreListCarousel />
        </div>

        <MerchantBasket
          v-if="openForOrders"
          ref="basket"
          :drawer-open="drawerOpen"
          :in-cart="showCartItemsOnly"
          :in-upsell="showUpsell"
          :sticky-mode="true"
          @show-cart="handleShowCart"
          @show-upsell="toggleUpsell"
        >
          <V1CartErrorBanner :cart-errors="v1CartErrors" @error-clicked="handleErrorClicked" />
          <PricingTierProgress
            v-show="!loading && !mealsStillToUse"
            v-fade-in
            :override-show="showCartItemsOnly"
            :persist="true"
          />
          <MealPlanProgress v-show="!loading" />
        </MerchantBasket>

        <CartDrawer
          v-if="drawerActive"
          :open="drawerOpen"
          :item-count="cartSize"
          :show-prices="showPrices"
          :totals="drawerTotals"
          :proceed-label="proceedLabel"
          :proceed-disabled="cartSize === 0 || !cartIsClear"
          @close="closeDrawer"
          @add-more="closeDrawer"
          @proceed="proceedFromDrawer"
        >
          <template #alerts>
            <V1CartErrorBanner :cart-errors="v1CartErrors" @error-clicked="handleDrawerErrorClicked" />
          </template>
          <template #progress>
            <PricingTierProgress v-show="!mealsStillToUse" :override-show="true" :persist="true" :compact="true" />
            <MealPlanProgress :compact="true" />
          </template>
          <template #items>
            <CartItems
              :cart-items="drawerCartItems"
              :bottle="bottle"
              :newest-first="true"
              :animated="true"
              :enforce-quantity-rules="true"
              :allow-sold-out-removal="true"
              :track-quantity-changes="true"
              :allow-edit="true"
              @edit-line="editDrawerLine"
            />
          </template>
        </CartDrawer>
      </div>

      <!-- questions -->
      <QuestionModal
        v-if="questionModalOpen"
        :title="currentSelectionQuestion?.attributes?.question"
        :subtitle="currentSelectionQuestion?.attributes?.secondary_info"
        :question-number="selectionQuestionNumber"
        :question-count="selectionQuestionCount"
        :content-key="currentSelectionQuestion.id"
      >
        <QuestionRow :question="currentSelectionQuestion" :editable="true" />
      </QuestionModal>

      <!-- upsells -->
      <UpsellModal
        v-if="upsellModalOpen"
        :header-text="upsellCopy.headerText"
        :button-text="upsellCopy.buttonText"
        :total-formatted="showPrices ? displayCartTotal : ''"
        :item-count="cartSize"
        :proceed-disabled="!cartIsClear"
        :proceed-label="upsellProceedLabel"
        :submitting="isActivelySubmittingCart"
        :hidden="productModalOpen"
        :layout="upsellLayout"
        @proceed="proceedFromUpsell"
        @close="toggleUpsell(false)"
      >
        <template #progress>
          <!-- v-if, not v-show: the progress card collapses on `:empty`, which a display:none child
               would defeat, leaving an empty bordered box in the modal. -->
          <PricingTierProgress v-if="!mealsStillToUse" :override-show="true" :persist="true" :compact="true" />
        </template>
        <CategoryProductStorePairContainer
          v-for="categoryProductStorePair in upsells"
          :key="categoryProductStorePair.id"
          :category-product-store-pair="categoryProductStorePair"
          :in-cart="false"
          :in-upsell="true"
        />
      </UpsellModal>
    </div>

    <!-- One host for the whole view: a modal rendered inside a card is clipped by
         ProductContainer's overflow-hidden and unmounted when its category collapses. -->
    <ProductModalHost v-if="hasCartModals" />
  </div>
</template>

<script>
import { isUpgradedTier } from '@/utils/pricingTierDiscount'
import QuestionRow from '@/components/Checkout/QuestionRow'
import QuestionModal from '@/components/Checkout/QuestionModal'
import UpsellModal from '@/components/Checkout/UpsellModal'
import CategoryProductStorePairContainer from '@/components/Merchant/CategoryProductStorePairContainer'
import { upsellCopyFromCpsps } from '@/utils/upsellCopy'
import { resolveUpsellLayout } from '@/utils/upsellLayout'
import { useProductModalOpen } from '@/composables/useProductModalOpen'
import PricingTierProgress from '@/components/Merchant/PricingTierProgress'
import MealPlanProgress from '@/components/Merchant/MealPlanProgress'
import mealPlanMealsStillAvailable from '@/utils/mealPlanMealsRemaining'
import ProgressPills from '@/components/Merchant/ProgressPills'

import {
  Loading,
  MerchantBasket,
  MerchantCategoriesBar,
  // MerchantFiltersBar,
  MerchantMain,
  MerchantNav
} from '@/components'
import TheBanner from '@/components/Molecules/TheBanner'
import StoreListCarousel from '@/components/Merchant/StoreListCarousel.vue'
import V1CartErrorBanner from '@/components/v1/V1CartErrorBanner.vue'
import CartDrawer from '@/components/CartDrawer/CartDrawer.vue'
import CartItems from '@/components/Checkout/CartItems'
import { totalsLinesFromCheckout } from '@/utils/orderSummaryLines'
import ProductModalHost, {
  restoreProductModalFromRoute,
  useProductModal
} from '@/components/Product/ProductModalHost.vue'
import { upsellLayoutOverride } from '@/router'
import { isCartDrawerDocked } from '@/utils/cartDrawerDock'
import { provide, ref } from 'vue'

export default {
  setup() {
    const highlightedProductId = ref(null)
    provide('v1HighlightedProductId', highlightedProductId)
    const { productModalOpen } = useProductModalOpen()
    const { openProductModal } = useProductModal()
    return { highlightedProductId, productModalOpen, openProductModal }
  },
  components: {
    MerchantMain,
    MerchantNav,
    MerchantBasket,
    MerchantCategoriesBar,
    // MerchantFiltersBar,
    Loading,
    TheBanner,
    QuestionRow,
    QuestionModal,
    UpsellModal,
    CategoryProductStorePairContainer,
    PricingTierProgress,
    MealPlanProgress,
    ProgressPills,
    StoreListCarousel,
    V1CartErrorBanner,
    CartDrawer,
    CartItems,
    ProductModalHost
  },
  data() {
    return {
      loading: true,
      messages: [],
      showCartItemsOnly: false,
      showUpsell: false,
      upsells: [],
      drawerOpen: false,
      questionSessionTotal: 0
    }
  },
  computed: {
    // BOT-1108 rollout gate. Off is production's inline card; on is the product modal.
    hasCartModals() {
      return this.$store.getters.hasCartModals
    },
    mealsStillToUse() {
      return mealPlanMealsStillAvailable(this.$store)
    },
    drawerEnabled() {
      return this.$store.getters.hasCartDrawer
    },
    drawerActive() {
      return this.drawerEnabled && this.openForOrders && !this.shouldDisplayQuestions
    },
    showPrices() {
      return this.bottle?.attributes?.show_prices !== false
    },
    drawerCartItems() {
      return this.$store.getters.cartItems || []
    },
    drawerTotals() {
      return totalsLinesFromCheckout(this.$store.getters.checkout?.attributes, {
        fulfillmentMethodType: this.$store.getters.fulfillmentMethod?.attributes?.type
      })
    },
    proceedLabel() {
      return this.bottleCycleDetailsForBottle?.autocharge ? 'Confirm order' : 'Proceed to checkout'
    },
    upsellProceedLabel() {
      return this.bottleCycleDetailsForBottle?.autocharge ? 'Confirm order' : 'Continue to checkout'
    },
    cartSize() {
      return this.$store.getters.cartSize
    },
    upsellCopy() {
      return upsellCopyFromCpsps(this.$store.state, this.upsells)
    },
    upsellModalOpen() {
      return this.showUpsell
    },
    upsellLayout() {
      return resolveUpsellLayout(upsellLayoutOverride())
    },
    displayCartTotal() {
      return this.$store.getters.currentCart?.attributes?.total_price?.format || ''
    },
    isActivelySubmittingCart() {
      return this.$store.state.activelySubmittingCart
    },
    bottle() {
      return this.$store.getters.currentBottle
    },
    // Cart-error strings for the banner — minus the "your cart is empty" one
    // (an empty cart is already conveyed by the disabled checkout button).
    v1CartErrors() {
      return (this.bottle?.attributes?.validations?.cart_errors || []).filter(
        (e) => typeof e === 'string' && !e.toLowerCase().includes('cart is empty')
      )
    },
    cartIsClear() {
      return !this.bottle?.attributes?.validations?.has_cart_errors
    },
    statusForPlacingOrders() {
      return this.bottle?.attributes?.status_for_placing_orders?.status
    },
    openForOrders() {
      return this.statusForPlacingOrders === 'open'
    },
    pricingTierID() {
      return this.$store.getters.currentCart?.relationships?.pricing_tier?.data?.id
    },
    hasQuestions() {
      return this.allSelectionQuestions.length > 0
    },
    shouldDisplayQuestions() {
      return this.hasQuestions && !this.bottleCycleIsExpired
    },
    questionModalOpen() {
      return !this.loading && this.shouldDisplayQuestions && !!this.currentSelectionQuestion
    },
    bottleCycleIsExpired() {
      return this.bottleCycleDetailsForBottle?.expired
    },
    bottleCycleDetailsForBottle() {
      return this.bottle?.attributes?.bottle_cycle_details
    },
    allSelectionQuestions() {
      return this.$store.getters.questions(this.bottle?.relationships?.selection_questions?.data) || []
    },
    currentSelectionQuestion() {
      return this.allSelectionQuestions[0] || null
    },
    selectionQuestionNumber() {
      return Math.max(1, this.questionSessionTotal - this.allSelectionQuestions.length + 1)
    },
    selectionQuestionCount() {
      return Math.max(this.questionSessionTotal, this.selectionQuestionNumber, this.allSelectionQuestions.length)
    },
    progressPills() {
      return [
        { status: 'current', to: { name: 'Selection' }, label: 'Selection' },
        !this.$store.getters.isLoggedIn && {
          status: 'upcoming',
          to: this.cartIsClear ? { name: 'Login' } : '',
          label: 'Information'
        },
        { status: 'upcoming', to: this.cartIsClear ? { name: 'Checkout' } : '', label: 'Checkout' }
      ].filter(Boolean)
    }
  },

  watch: {
    questionModalOpen: {
      // Snapshot how many questions this session will ask so the "Question X of Y"
      // header stays stable while the server-driven selection_questions list
      // shrinks with each answer.
      handler(val) {
        this.questionSessionTotal = val ? this.allSelectionQuestions.length : 0
      },
      immediate: true
    },
    pricingTierID: function (newPricingTierID, oldPricingTierID) {
      let newPricingTier = this.pricingTierWithID(newPricingTierID)
      let oldPricingTier = this.pricingTierWithID(oldPricingTierID)
      if (oldPricingTier && newPricingTier && isUpgradedTier(newPricingTier, oldPricingTier)) {
        this.notifyAboutPricingTier(newPricingTier)
      }
    }
  },
  async mounted() {
    window.scrollTo(0, 0)
    if (!this.$store.getters.bottleId || this.$store.getters.bottleId == 'undefined') {
      this.$router.push({ name: 'Home' })
      return
    } else {
      // Kick off the ancillary store data in parallel with the bottle. A failure
      // here must NOT strand the customer on the store home: a store-less bottle
      // (a custom invoice) legitimately 404s on getStore, and handleStatus
      // redirects those straight to Checkout. Only an unresolved *bottle* falls
      // back to Home (see the getBottle catch below). Swallow store-fetch errors
      // so they can't trip that fallback. BOT-1139.
      const storeFetches = Promise.all([
        this.$store.dispatch('getStoreDetailsForBottle'),
        this.$store.dispatch('getStore')
      ]).catch(() => {})

      try {
        await this.$store.dispatch('getBottle', {
          includes: [
            'category_product_store_pairs',
            'product_price_list_pairs',
            'cart',
            'cart.cart_items',
            'cart.cart_items.cart_item_variant_options',
            'cart.cart_items.cart_item_variant_options.variant_option',
            'cart.cart_items.product',
            'cart.membership_tier',
            'cart.pricing_tier',
            'cart.next_pricing_tier',
            'cart.available_pricing_tiers',
            'fulfillment_slot',
            'fulfillment_slot.fulfillment_method',
            'merchant',
            'customer',
            'membership_tier',
            'distribution_list',
            'selection_questions',
            'removed_category_product_store_pairs_still_in_cart',
            'removed_category_product_store_pairs_still_in_cart.product',
            // The package the bottle is already on, and the preferred option it names per modifier
            // group. The modal opens a blank configuration on those, so they have to be resolved
            // before the first card is tapped - not only after the customer switches package.
            'package',
            'package.variant_preference_metadata'
          ],
          temporaryActualUltramarineEnable: true
        })
      } catch (e) {
        // getBottle already tries to recover from a broken anonymous token. If
        // the bottle still can't be resolved, fail gracefully to the store home
        // rather than leaving the customer on a blank screen. BOT-973.
        this.loading = false
        this.$router.push({ name: 'Home' })
        return
      }

      this.handleAuthorization()
      this.handleStatus()
      this.$store.dispatch('sendUtmAttribution')
      // Fire-and-forget: populates the real `customer` type (merchant/customer
      // endpoint) with the meal_plan_subscription relationship MealPlanProgress
      // reads. The bottle's own `customer` include resolves to the `user` type,
      // which has no such relationship - getCustomerInfo already no-ops
      // gracefully on anonymous visitors (400), matching V2Selection.vue/
      // Checkout.vue's unguarded dispatch.
      this.$store.dispatch('getCustomerInfo', ['meal_plan_subscription', 'meal_plan_subscription.meal_plan'])

      await storeFetches

      if (this.drawerEnabled) {
        // Populate the checkout totals (Subtotal/Taxes/Today's Total) before
        // the drawer first paints — awaited so a fast first add can't race a
        // stale bottle snapshot over the fresh cart. Subsequent cart submits
        // keep them fresh via force_checkout (see updateSubmittableCart).
        await this.$store.dispatch('getBottleCheckout', { includeCheckout: true }).catch(() => {})
      }

      this.loading = false
      this.$store.commit('SET_MAIN_CONTENT_LOADED', true)
      if (this.$route.query.showCart && this.$route.query.showCart === 'y') {
        if (this.drawerActive) {
          this.openDrawer()
        } else {
          this.toggleShowCartItemsOnly(true)
        }
      }
      // Deep link into the product modal. Runs here, not in the host, because the host mounts
      // before `getBottle` resolves and V1 resolves products out of Vuex - at mount there is
      // nothing to resolve. An unknown slug silently strips itself and leaves the store rendered.
      if (this.$store.getters.hasCartModals) {
        restoreProductModalFromRoute(this.$store, this.$router, this.$route)
      }
    }
  },

  methods: {
    handleAuthorization() {
      if (!this.$store.getters.authorizedForBottle(this.bottle)) {
        this.$router.push({ name: 'Home' })
      }
    },
    handleStatus() {
      const status = this.bottle?.attributes?.status
      if (status == 'paid' || status == 'invoiced') {
        this.$router.push({ name: 'Receipt' })
      } else if (status == 'skipped') {
        this.$router.push({ name: 'Notice' })
      } else if (this.bottle && !this.bottle.attributes.supports_selection_screen) {
        this.$router.replace({ name: 'Checkout' })
      }
    },
    pricingTierWithID(pricingTierID) {
      return this.$store.getters.pricingTierWithID(pricingTierID)
    },
    handleDismissedBanner(bannerIndex) {
      this.messages.splice(bannerIndex, 1)
      if (this.messages.length > 0) this.messages[0].uid = Math.random()
    },
    notifyAboutPricingTier(pricingTier) {
      this.resetBanner()
      this.messages.push({
        status: 'brand',
        position: 'fixed',
        title: 'Unlocked ' + pricingTier.attributes?.title,
        useEmoji: '💸',
        message: pricingTier.attributes?.subtitle,
        dismissTimeout: 6000,
        uid: Math.random()
      })
    },
    resetBanner() {
      this.messages = []
    },
    toggleShowCartItemsOnly(setTo) {
      this.showCartItemsOnly = setTo
      if (this.showCartItemsOnly) {
        window.scrollTo(0, 0)
      }
    },
    handleShowCart(setTo) {
      if (this.drawerActive) {
        this.openDrawer()
      } else {
        this.toggleShowCartItemsOnly(setTo)
      }
    },
    openDrawer() {
      this.drawerOpen = true
      this.showCartItemsOnly = false
    },
    closeDrawer() {
      this.drawerOpen = false
    },
    editDrawerLine(item) {
      const frontendKey = item?.attributes?.frontend_key
      const submittableCartItem = (this.$store.getters.submittableCart || []).find(
        (line) => line.frontend_key === frontendKey
      )
      if (!submittableCartItem) return
      this.openProductModal({
        productId: submittableCartItem.product_id ?? item.relationships?.product?.data?.id,
        categoryProductStorePairId: submittableCartItem.category_product_store_pair_id,
        submittableCartItem
      })
    },
    proceedFromDrawer() {
      this.$refs.basket?.clickedProceed()
    },
    handleDrawerErrorClicked(errorString) {
      if (!isCartDrawerDocked()) {
        this.closeDrawer()
      }
      this.handleErrorClicked(errorString)
    },
    showFullView() {
      this.showCartItemsOnly = false
      this.showUpsell = false
    },
    async toggleUpsell(visible) {
      this.showUpsell = visible
      if (!visible) return

      this.showCartItemsOnly = false
      this.drawerOpen = false

      this.upsells = (await this.$store.dispatch('getUpsells'))?.data || []
    },
    // Both footer actions mean "on to checkout". The basket's own guard sees inUpsell and skips
    // straight past the upsell branch it opened this modal from.
    proceedFromUpsell() {
      this.$refs.basket?.clickedProceed()
    },
    triggerHighlight(productId) {
      this.highlightedProductId = productId
      if (this._highlightTimer) clearTimeout(this._highlightTimer)
      this._highlightTimer = setTimeout(() => {
        this.highlightedProductId = null
      }, 2000)
    },
    handleErrorClicked(errorString) {
      const products = Object.values(this.$store.state.objects?.product || {})
      const matching = products
        .filter((p) => p?.attributes?.product_name)
        .sort((a, b) => b.attributes.product_name.length - a.attributes.product_name.length)
        .find((p) => errorString.includes(p.attributes.product_name))
      if (!matching) return
      const id = matching.id
      const el = document.querySelector(`[data-product-id="${id}"]`)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      this.triggerHighlight(id)
    }
  },
  beforeUnmount() {
    if (this._highlightTimer) clearTimeout(this._highlightTimer)
  }
}
</script>
